import { MButton } from "@app/components";
import { Box, Grid, IconButton } from "@mui/material";
import React, { useEffect, useMemo } from "react";
import {
  columnsAnalisisCurvaInputs,
  columnsAnalisisCurvaResult,
} from "../../utils/calibration.columns";
import {
  calculateEfficiency,
  calculateEfficiencyFinal,
  recalculateAndUpdateFunction,
} from "../../utils/helpers";
import { initialRowsCurva } from "../../utils/curva.rows";
import { MODES_OPTIONS } from "@app/types/generics.types";
import ChartAbsorptionFactor from "../graphics";
import TableResult from "../tableResult";
import { useFormContext } from "react-hook-form";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { recalcularCoeficientes } from "../../utils/calculations";
import { errorAlert } from "@app/utils/alerts";
import DeleteIcon from "@mui/icons-material/Delete";

interface AnalysisFormProps {
  mode: string;
  nameTable?: string;
}

const ChartAbsorptionForm = ({
  mode,
  nameTable = "rowsCurva",
}: AnalysisFormProps) => {
  const { watch, setValue, getValues } = useFormContext();

  const rowsCurva = watch(nameTable) || initialRowsCurva;
  const isView = mode === MODES_OPTIONS.VIEW;

  // Calcular resultados automáticamente cuando se carguen las filas con datos válidos
  useEffect(() => {
    if (rowsCurva && rowsCurva.length >= 2) {
      try {
        const validRows = rowsCurva.filter(
          (row) => row.sqp_tsie_quenching !== "" && row.sqp_tsie_quenching !== 0 &&
                   row.fa_exp !== "" && row.fa_exp !== 0
        );
        
        if (validRows.length >= 2) {
          recalculateAndUpdate(rowsCurva);
        }
      } catch (error) {
        console.error("Error al calcular resultados iniciales:", error);
      }
    }
  }, [rowsCurva?.length]); // Solo se ejecuta cuando cambia el número de filas

  const recalculateAndUpdate = (updatedRows) => {
    try {
      const resultsData = recalculateAndUpdateFunction(updatedRows);
      setValue(`${nameTable}-results`, resultsData);
    } catch (error) {
      console.error("Error al recalcular coeficientes: ", error);
    }
  };

  const handleDeleteRow = (id: string | number) => {
    // Verificar que haya al menos 3 filas antes de eliminar (para dejar mínimo 2)
    if (rowsCurva.length <= 2) {
      errorAlert("Debe mantener al menos 2 filas en la tabla");
      return;
    }
    
    const updatedRows = rowsCurva.filter((row) => row.id !== id);
    
    // Verificar nuevamente después del filtro
    if (updatedRows.length < 2) {
      errorAlert("Debe mantener al menos 2 filas en la tabla");
      return;
    }
    
    setValue(nameTable, updatedRows, { shouldDirty: true });
    
    // Recalcular los resultados solo si hay suficientes datos válidos
    try {
      const validRows = updatedRows.filter(
        (row) => row.sqp_tsie_quenching !== "" && row.fa_exp !== ""
      );
      if (validRows.length >= 2) {
        recalculateAndUpdate(updatedRows);
      }
    } catch (error) {
      console.error("Error al recalcular después de eliminar:", error);
    }
  };

  // Crear columnas dinámicas con botón de eliminar solo para filas adicionales
  const columnsResultWithDelete: GridColDef[] = useMemo(() => {
    if (isView) {
      return columnsAnalisisCurvaResult;
    }

    return [
      ...columnsAnalisisCurvaResult,
      {
        field: "actions",
        headerName: "Acciones",
        width: 100,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          return (
            <IconButton
              size="small"
              onClick={() => handleDeleteRow(params.row.id)}
              color="error"
              title="Eliminar fila"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          );
        },
      },
    ];
  }, [isView, rowsCurva]);

  const processRowUpdate = (newRow) => {
    const updatedRows = calculateEfficiency(newRow, rowsCurva);
    recalculateAndUpdate(updatedRows);
    setValue(nameTable, updatedRows);
    return newRow;
  };

  const handleCalculate = () => {
    try {
      const clonedUpdatedRows = JSON.parse(
        JSON.stringify(getValues(nameTable))
      ).filter((row) => row.sqp_tsie_quenching !== "" && row.fa_exp !== "");

      if (clonedUpdatedRows.length < 2) {
        return;
      }
      const puntosActualizados = clonedUpdatedRows.map((row) => ({
        x: parseFloat(row.sqp_tsie_quenching),
        y: parseFloat(row.fa_exp),
      }));

      const { coeficientesNumericos } =
        recalcularCoeficientes(puntosActualizados);

      const updatedRows = calculateEfficiencyFinal(
        getValues(nameTable),
        coeficientesNumericos
      );

      setValue(nameTable, updatedRows);
    } catch (error) {
      console.error("Error al calcular: ", error);
      errorAlert("Error al calcular");
    }
  };

  const handleAddRow = () => {
    const currentRows = rowsCurva || [];
    const newRow = {
      sqp_tsie_quenching: 0,
      efficiency: 0,
      fa_exp: 0,
      fc_calc: 0,
      id: `new-${currentRows.length + 1}`,
    };
    setValue(nameTable, [...currentRows, newRow]);
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box sx={{ width: "100%", display: "flex", gap: 0 }}>
              <Box sx={{ flex: 1 }}>
                <DataGrid
                  key={"data-input"}
                  rows={rowsCurva || initialRowsCurva}
                  columns={columnsAnalisisCurvaInputs}
                  disableRowSelectionOnClick
                  processRowUpdate={processRowUpdate}
                  hideFooterPagination
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <DataGrid
                  key={"data-result"}
                  rows={rowsCurva || initialRowsCurva}
                  columns={columnsResultWithDelete}
                  disableRowSelectionOnClick
                  processRowUpdate={processRowUpdate}
                  hideFooterPagination
                />
              </Box>
            </Box>
            {!isView && (
              <Box
                sx={{ mt: 2, display: "flex", flexDirection: "row", gap: 2 }}
              >
                <MButton
                  onClick={handleAddRow}
                  text="Agregar fila"
                  variant="outlined"
                />
                <MButton onClick={handleCalculate} text="Calcular" />
                <MButton
                  onClick={() => setValue(nameTable, initialRowsCurva)}
                  text="Limpiar"
                  variant="outlined"
                />
              </Box>
            )}
          </Box>
        </Grid>
        <Grid item xs={6}>
          <ChartAbsorptionFactor rows={rowsCurva} />
        </Grid>
      </Grid>
      <TableResult results={watch(`${nameTable}-results`)} />
    </Box>
  );
};

export default ChartAbsorptionForm;
